import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState, type MouseEvent } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { useAppStores } from "./app-context";
import { AstrialLogo } from "./astrial-logo";
import { ConnectivityStatus } from "./connectivity-status";
import { EngageLogo } from "./engage-logo";
import { ProjectInformation } from "./branding/project-information";
import { IncidentContextBar } from "./incident-context-bar";
import { SessionControls } from "./session/session-controls";
import { iconForModule, ShellIcon } from "./shell-icons";
import { HOME_LANDING_MODULE } from "./shell/role-landing";
import { useWorkspaceLayoutActions } from "./shell/workspace-layout-actions";
import { themeCodes } from "./theme-store";
import { workContextLabel } from "./work-context-labels";
import { operationalWorkflowDestinations } from "./workflow-navigation";
import {
  notificationActionLabel,
  notificationNavigationTarget,
  notificationTone,
  presentNotification,
  type NotificationTone,
} from "./notification-presentation";
import { Button } from "../ui/button";
import { EntityPhoto } from "../ui/entity-photo";
import { mediaUrlForKey } from "../platform/media-key";
import { ProfilePhotoEditor } from "./session/profile-photo-editor";
import {
  OperationalIcon,
  type OperationalIconName,
} from "../ui/operational-icon";
import { workspaceText } from "../workspace/i18n";
import type { WorkspaceComponentId } from "../workspace";
import {
  headerWorkspaceEntriesForRole,
  isOperationsFlexWorkspaceId,
  operationsWorkspaceComponentIsRelevant,
  operationsWorkspaceForPanel,
  perspectiveDefinition,
  normalizeRoleCode,
  rolePerspectiveAssignment,
  rolePerspectiveAssignmentsForRoles,
  type OperationalWorkspaceId,
  type PerspectiveTarget,
} from "../features/operations/role-perspective-registry";
import { operationsComponentRegistry } from "../features/operations/operations-component-registry";

const themeMessageKeys = {
  "dark-blue": "theme_dark_blue",
  grey: "theme_grey",
  "high-contrast": "theme_high_contrast",
  light: "theme_light",
} as const;

const notificationIcons: Record<NotificationTone, OperationalIconName> = {
  danger: "warning",
  info: "info",
  neutral: "circle",
  warning: "warning",
};

interface HeaderModuleItem {
  readonly id: string;
  readonly icon?: import("./shell-icons").ShellIconName;
  readonly operationalIcon?: OperationalIconName | undefined;
  readonly label: string;
  readonly to: string;
  readonly workspaceId?: OperationalWorkspaceId;
  readonly focusPanelId?: string;
  readonly focusScreen?: string;
  readonly onSelect?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

function HeaderModuleGlyph({ item }: { readonly item: HeaderModuleItem }) {
  return item.operationalIcon === undefined
    ? <ShellIcon name={item.icon ?? iconForModule(item.id)} />
    : <OperationalIcon name={item.operationalIcon} />;
}

const OPERATIONS_PANEL_BY_FEATURE_ID: Readonly<Record<string, string>> = {
  collaboration: "collaboration",
  forms: "incident-reporting",
  lagekarte: "situation-map",
  logistics: "logistics",
  "operational-log": "etb-log",
  operations: "workflow-board",
  readiness: "readiness-status",
  registration: "mobilization",
  "resource-requests": "resource-requests",
  "situation-mapping": "situation-map",
};

/**
 * A workspace module whose header entry carries a different id than the feature
 * it opens — Fähigkeiten is the workflow entry for `capability-resources`.
 * Without this the header would stop marking the current entry once the module
 * moved from `/modules/…` into the shared workspace at `/?open=…`.
 */
const headerItemForModule: Readonly<Record<string, string>> = {
  "capability-resources": "workflow-capabilities",
};

/**
 * Where "take me Home" points — the header's Home entry, the mobile bottom-nav
 * Home entry and the brand lockup.
 *
 * A bare `/` cannot express that intent. The index resolves the ROLE's landing
 * module (`shell/role-landing.ts`) whenever no `?open=` is present and redirects
 * operations-, map- and log-first roles to their own board, so Home was
 * unreachable for exactly the roles that have one: clicking Home from
 * `/operations` navigated to `/` and was bounced straight back, leaving the
 * Operations entry marked current. An explicit `?open=` always wins over the
 * landing rule, so state the module. Bare `/` keeps its own meaning — "wherever
 * this session works" — for sign-in and bookmarks.
 */
const HOME_ROUTE = `/?open=${HOME_LANDING_MODULE}`;

function perspectiveTargetPath(
  target: PerspectiveTarget,
  currentSearch?: string,
): string {
  const search = new URLSearchParams();
  if (target.workspaceId !== undefined) search.set("workspace", target.workspaceId);
  if (target.component !== undefined) search.set("panel", target.component);
  if (target.screen !== undefined) search.set("screen", target.screen);
  // Completion owns process/phase as URL state, not layout state. Carry it
  // across workspace shortcuts so returning to CLOSE resumes the same cycle.
  if (
    (target.path === "/operations" || target.path === "/logistics") &&
    currentSearch !== undefined
  ) {
    const current = new URLSearchParams(currentSearch);
    for (const key of ["process", "phase"] as const) {
      const value = current.get(key);
      if (value !== null) search.set(key, value);
    }
  }
  const query = search.toString();
  return query === "" ? target.path : `${target.path}?${query}`;
}

function uniqueItems(items: readonly HeaderModuleItem[]): HeaderModuleItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function HeaderClock({
  label,
  localeCode,
}: {
  readonly label: string;
  readonly localeCode: "de" | "en";
}) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const interval = window.setInterval(() => {
      setNow(new Date());
    }, 1_000);
    return () => {
      window.clearInterval(interval);
    };
  }, []);

  return (
    <time className="shell-clock" dateTime={now.toISOString()} title={label}>
      {new Intl.DateTimeFormat(localeCode === "de" ? "de-DE" : "en-GB", {
        hour: "2-digit",
        hour12: false,
        minute: "2-digit",
        second: "2-digit",
      }).format(now)}
    </time>
  );
}

export const AppShell = observer(function AppShell() {
  const {
    api,
    features,
    locale,
    notifications,
    operationalContext,
    session,
    status,
    theme,
  } = useAppStores();
  const location = useLocation();
  const navigate = useNavigate();
  const [fullscreen, setFullscreen] = useState(false);
  const notificationsMenuRef = useRef<HTMLDetailsElement>(null);
  const moreMenuRef = useRef<HTMLDetailsElement>(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const { actions: workspaceLayoutActions } = useWorkspaceLayoutActions();

  /*
   * This is deliberately derived on every observer render rather than hidden
   * behind `useMemo([features, locale, session])`. Those three values are
   * long-lived MobX store objects: their references do not change when a
   * capability refresh replaces `allowedViewIds`/`permissions`. Memoizing on
   * the store references therefore froze the header at the anonymous or
   * previous-role menu after login/context switching. The descriptor list is
   * small, while reading it here lets MobX track the actual observable
   * capability and locale values.
   */
  const assignment = rolePerspectiveAssignment(session.activeContext?.roleCode);
  const assessmentOnly = normalizeRoleCode(session.activeContext?.roleCode ?? "") === "technical_adviser";
  const assessmentPath = "/modules/adviser-field";
  const perspective = assignment?.perspective === null || assignment?.perspective === undefined
    ? null
    : perspectiveDefinition(assignment.perspective);
  const roleHeaderFeatures = perspective === null
    ? null
    : new Set(perspective.headerFeatureIds);
  const discoverableFeatures = features.discoverable();
  const discoverableFeatureIds = new Set(discoverableFeatures.map((item) => item.id));
  const desktopWorkspaceItems: readonly HeaderModuleItem[] =
    headerWorkspaceEntriesForRole(session.activeContext?.roleCode)
      .filter((entry) => {
        if (features.resolve(entry.gateFeatureId).status !== "allowed") return false;
        if (!session.canDiscover(entry.requiredPermissions)) return false;
        const component = entry.target.component;
        if (component === undefined) return true;
        const panel = operationsComponentRegistry.get(component);
        if (panel === undefined || !session.canDiscover(panel.requiredPermissions)) return false;
        if (
          assignment?.operationsPerspective !== null &&
          assignment?.operationsPerspective !== undefined &&
          isOperationsFlexWorkspaceId(entry.workspaceId)
        ) {
          return operationsWorkspaceComponentIsRelevant(
            assignment.operationsPerspective,
            entry.workspaceId,
            component,
          );
        }
        return true;
      })
      .map((entry): HeaderModuleItem => ({
        ...(entry.target.component === undefined
          ? {}
          : { focusPanelId: entry.target.component }),
        ...(entry.target.screen === undefined
          ? {}
          : { focusScreen: entry.target.screen }),
        icon: entry.icon,
        ...(entry.operationalIcon === undefined
          ? {}
          : { operationalIcon: entry.operationalIcon }),
        id: entry.id,
        label: locale.text(entry.labelKey),
        to: perspectiveTargetPath(entry.target, location.search),
        workspaceId: entry.workspaceId,
      }));
  // A specialist's preferred feature may be absent under a narrower atomic
  // grant. Brand the first feature the user can actually discover as the
  // perspective destination instead of silently falling back to a generic
  // module label/icon.
  const primaryPerspectiveFeature = perspective?.headerFeatureIds.find((featureId) =>
    discoverableFeatureIds.has(featureId)) ?? null;
  const featureItems = discoverableFeatures
    .filter(
      (descriptor) =>
        descriptor.id !== "mission-console" &&
        (roleHeaderFeatures === null
          ? descriptor.administrationSection === undefined &&
            descriptor.operationsSection === undefined
          : roleHeaderFeatures.has(descriptor.id)),
    )
    .sort((left, right) => perspective === null
      ? 0
      : perspective.headerFeatureIds.indexOf(left.id) -
        perspective.headerFeatureIds.indexOf(right.id))
    .map((descriptor): HeaderModuleItem => {
      const isPrimaryPerspective = perspective !== null &&
        descriptor.id === primaryPerspectiveFeature;
      const defaultTarget = assignment?.defaultTarget ?? null;
      // A perspective that owns its own workspace route (LOGISTICS →
      // `/logistics`) keeps its secondary panels on that route too, so the
      // role's icons stay in one workspace and each still highlights. Every
      // other perspective targets `/operations` exactly as before.
      const workspaceBasePath = defaultTarget?.kind === "operations-panel"
        ? defaultTarget.path
        : "/operations";
      const operationsPanel = OPERATIONS_PANEL_BY_FEATURE_ID[descriptor.id];
      return {
        id: descriptor.id,
        icon: isPrimaryPerspective ? perspective.icon : iconForModule(descriptor.id),
        label: isPrimaryPerspective
          ? perspective.label[locale.code]
          : descriptor.title[locale.code],
        // Operations is the standalone `/operations` FlexLayout workspace
        // (workflow board, mission orders, mobilization, deployment planning,
        // …), not the thin Hub-embedded panel the generic `open=` mechanism
        // below would otherwise resolve to — that embed only ever carried a
        // fraction of the registered Operations panels. No `incidentId` on the
        // link: `OperationalContextStore` already remembers the active
        // principal's selected incident itself (`preferredStoredIncidentId`,
        // restored from session storage in `load()`), so the route re-selects
        // it on its own without the id ever needing to travel through the URL.
        to: isPrimaryPerspective && defaultTarget !== null
          ? perspectiveTargetPath(defaultTarget)
          : operationsPanel !== undefined && session.canDiscover(["mission_read"])
            ? `${workspaceBasePath}?panel=${encodeURIComponent(operationsPanel)}`
            : descriptor.id === "collaboration" &&
                operationalContext.selection.incidentId !== null
              ? `/?open=${descriptor.id}&incidentId=${encodeURIComponent(operationalContext.selection.incidentId)}`
              : `/?open=${descriptor.id}`,
      };
    });
  const availableRoleCodes = [
    ...session.effectiveRoleIds,
    ...session.availableContexts
      .filter((context) => context.isActive)
      .map((context) => context.roleCode),
  ];
  const perspectiveItems = rolePerspectiveAssignmentsForRoles(
    availableRoleCodes,
    session.activeContext?.roleCode,
  )
    .filter((candidate) =>
      candidate.perspective !== assignment?.perspective &&
      candidate.defaultTarget !== null &&
      session.canDiscover(candidate.requiredPermissions) &&
      discoverableFeatureIds.has(
        perspectiveDefinition(candidate.perspective as NonNullable<typeof candidate.perspective>)
          .headerFeatureIds[0] ?? "",
      ) && (
        [...session.effectiveRoleIds].some((roleCode) =>
          normalizeRoleCode(roleCode) === candidate.roleCode) ||
        session.availableContexts.some((context) =>
          context.isActive &&
          normalizeRoleCode(context.roleCode) === candidate.roleCode &&
          context.organizations.length > 0)
      ))
    .map((candidate): HeaderModuleItem => {
      const definition = perspectiveDefinition(
        candidate.perspective as NonNullable<typeof candidate.perspective>,
      );
      const destination = perspectiveTargetPath(candidate.defaultTarget as PerspectiveTarget);
      const context = session.availableContexts.find((available) =>
        available.isActive &&
        normalizeRoleCode(available.roleCode) === candidate.roleCode &&
        available.grantId !== session.activeContext?.grantId,
      );
      const currentOrganization = session.activeOrganizationUnit?.id;
      const organization = context?.organizations.find((item) => item.unitId === currentOrganization)
        ?? context?.organizations[0];
      return {
        icon: definition.icon,
        id: `perspective-${definition.id.toLowerCase()}`,
        label: definition.label[locale.code],
        to: destination,
        ...(context === undefined || organization === undefined
          ? {}
          : {
              onSelect: (event: MouseEvent<HTMLAnchorElement>): void => {
                event.preventDefault();
                void session.switchContext(api, context.grantId, organization.unitId)
                  .then(() => navigate(destination));
              },
            }),
      };
    });
  if (
    perspective !== null &&
    assignment?.defaultTarget?.path === "/operations" &&
    session.canDiscover(["mission_read"]) &&
    !featureItems.some((item) => item.id === primaryPerspectiveFeature)
  ) {
    // The Operations descriptor deliberately is not a mobile shell feature,
    // because phones use the task-first stack rather than FlexLayout. Keep the
    // active role perspective visible in the compact header anyway; the direct
    // /operations route selects the supported mobile stack.
    featureItems.unshift({
      icon: perspective.icon,
      id: "operations",
      label: perspective.label[locale.code],
      to: perspectiveTargetPath(assignment.defaultTarget),
    });
  }
  // A perspective's primary feature (`isPrimaryPerspective` above) must keep
  // its own perspective label, icon and target even when the underlying
  // feature id also has a generic quick link below (`operations`,
  // `incident-intake`, …). Otherwise the id collision always resolved in
  // favor of the generic workflow entry — appended first, and never removed
  // from `featureItems` since `workflowFeatureIds` unconditionally dropped
  // any feature sharing its id — so Command, LuK intake, Regional
  // coordination and every other perspective whose primary feature happens
  // to be `operations`/`incident-intake` under a narrower grant could never
  // brand its own primary header link; the header silently fell back to the
  // feature's generic title with a same-looking (but perspective-blind) URL.
  const workflowItems: HeaderModuleItem[] = [...operationalWorkflowDestinations({
    canDiscover: session.canDiscover,
    discoverableFeatureIds,
    locale: locale.code,
  })].filter((item) => item.id !== primaryPerspectiveFeature);
  const workflowFeatureIds = new Set([
    "incident-intake",
    "incidents",
    "adviser-field",
    "operations",
    "orders",
    "resource-requests",
  ]);
  const primaryFeatureItems = featureItems.filter((item) => item.id === primaryPerspectiveFeature);
  const supportingFeatureItems = featureItems.filter((item) =>
    item.id !== primaryPerspectiveFeature && !workflowFeatureIds.has(item.id));
  const items: HeaderModuleItem[] = assessmentOnly
    ? featureItems.filter((item) => item.id === "adviser-field")
    : [
        { id: "home", label: locale.text("homeNav"), to: HOME_ROUTE },
        // Keep the active role's main destination first. Generic workflow
        // shortcuts then follow in process order, with role-specific awareness
        // and collaboration tools after them. This prevents a broad grant from
        // pushing the operator's actual work behind unrelated workflow icons.
        ...primaryFeatureItems,
        ...workflowItems,
        ...supportingFeatureItems,
        ...perspectiveItems,
      ];
  if (!assessmentOnly) {
    items.push({
      id: "modules",
      label: locale.text("modulesNav"),
      to: "/?open=modules",
    });
  }
  // A role may declare a hard header allowlist (see `headerItemIds`). Applied
  // last so it trims whatever the perspective, workflow and permission filters
  // produced, and ordered by the allowlist itself rather than by build order.
  const headerItemAllowlist = assignment?.headerItemIds ?? null;
  const allowedItems = headerItemAllowlist === null
    ? items
    : headerItemAllowlist
      .map((id) => items.find((item) => item.id === id))
      .filter((item): item is HeaderModuleItem => item !== undefined);
  const operationsHeaderHiddenItems = new Set(["lagekarte", "forms", "operational-log"]);
  const legacyModuleItems: readonly HeaderModuleItem[] = uniqueItems(allowedItems).filter(
    (item) => location.pathname !== "/operations" || !operationsHeaderHiddenItems.has(item.id),
  );
  // Phones keep the established field/stacked navigation unchanged. Desktop
  // and tablet use the active role's ordered workspace entries.
  const moduleItems = session.clientDevice === "mobile" || desktopWorkspaceItems.length === 0
    ? legacyModuleItems
    : desktopWorkspaceItems;
  const mobileModuleItems = legacyModuleItems.slice(0, 5);
  const routeOwnsMobileNavigation = location.pathname === "/modules/field-operations";
  /*
   * Workspace contract: the command header carries at most the FIRST 5
   * tabs; every further module stays reachable through the trailing
   * "Mehr ▾"/"More ▾" popover (same details pattern as notifications).
   */
  const primaryModuleItems = moduleItems.slice(0, 5);
  const overflowModuleItems = moduleItems.slice(5);

  const locationSearch = new URLSearchParams(location.search);
  const requestedModule = locationSearch.get("open");
  const requestedPanel = locationSearch.get("panel");
  const requestedScreen = locationSearch.get("screen");
  const requestedWorkspace = locationSearch.get("workspace");
  const legacyActiveModule =
    (requestedModule === null
      ? null
      : headerItemForModule[requestedModule] ?? requestedModule) ??
    (location.pathname.startsWith("/administration")
      ? "administration"
      // `/logistics` is the quartermaster workspace. Its panels map to their
      // own header features so each icon lights up on its own screen, which
      // `/operations`' mapping below could never do for them: `asset-directory`
      // is absent from that chain, so it fell through to "operations" and the
      // Logistics icon never highlighted.
      : location.pathname.startsWith("/logistics")
        ? requestedPanel === "readiness-status"
          ? "readiness"
          : requestedPanel === "mobilization"
            ? "registration"
            : "logistics"
      : location.pathname.startsWith("/operations")
        ? requestedPanel === "deployment-planning" ||
              requestedPanel === "resource-requests" ||
              requestedPanel === "readiness-status" ||
              requestedPanel === "feature-regional-coordination"
            ? "workflow-capabilities"
            : requestedPanel === "mission-orders" || requestedPanel === "legacy-mission-orders"
              ? "orders"
              : "operations"
        : location.pathname.startsWith("/modules/capability-resources")
          ? "workflow-capabilities"
        : location.pathname.startsWith("/incidents")
          ? "incidents"
          : location.pathname.startsWith("/modules/")
            ? location.pathname.split("/")[2] ?? "modules"
            : location.pathname.startsWith("/modules")
              ? "modules"
              : "home");
  const resolvedActiveWorkspace = isOperationsFlexWorkspaceId(requestedWorkspace)
    ? requestedWorkspace
    : assignment?.operationsPerspective !== null &&
        assignment?.operationsPerspective !== undefined &&
        requestedPanel !== null &&
        operationsComponentRegistry.has(requestedPanel as WorkspaceComponentId)
      ? operationsWorkspaceForPanel(
          requestedPanel as WorkspaceComponentId,
          assignment.operationsPerspective,
        )
      : null;
  const exactWorkspaceEntry = resolvedActiveWorkspace === null
    ? null
    : desktopWorkspaceItems.find((item) =>
        item.workspaceId === resolvedActiveWorkspace &&
        item.focusPanelId === requestedPanel &&
        (item.focusScreen ?? null) === requestedScreen);
  const genericFocusedWorkspaceEntry = resolvedActiveWorkspace === null
    ? null
    : desktopWorkspaceItems.find((item) =>
        item.workspaceId === resolvedActiveWorkspace &&
        item.focusPanelId === requestedPanel &&
        item.focusScreen === undefined);
  const firstWorkspaceEntry = resolvedActiveWorkspace === null
    ? null
    : desktopWorkspaceItems.find((item) => item.workspaceId === resolvedActiveWorkspace);
  const routeWorkspaceEntry = desktopWorkspaceItems.find((item) =>
    item.to.split("?", 1)[0] === location.pathname);
  const activeModule = session.clientDevice === "mobile" || desktopWorkspaceItems.length === 0
    ? legacyActiveModule
    : exactWorkspaceEntry?.id ??
      genericFocusedWorkspaceEntry?.id ??
      firstWorkspaceEntry?.id ??
      routeWorkspaceEntry?.id ??
      desktopWorkspaceItems[0]?.id ??
      legacyActiveModule;

  // The M23 inbox is session-scoped; AppShell only renders authenticated.
  useEffect(() => notifications.start(), [notifications]);

  useEffect(() => {
    function onFullscreenChange(): void {
      setFullscreen(document.fullscreenElement !== null);
    }
    document.addEventListener("fullscreenchange", onFullscreenChange);
    onFullscreenChange();
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, []);

  function toggleFullscreen(): void {
    if (
      document.fullscreenElement === null &&
      typeof document.documentElement.requestFullscreen === "function"
    ) {
      void document.documentElement.requestFullscreen().catch(() => undefined);
    } else if (typeof document.exitFullscreen === "function") {
      void document.exitFullscreen().catch(() => undefined);
    }
  }

  const profileLabel = locale.text("shellProfileMenuLabel");
  const profileInitial = session.identity?.displayName.slice(0, 1).toUpperCase() ?? null;

  // The field-adviser role is intentionally a single-purpose application
  // surface. Hiding navigation alone would still expose Home, Modules and
  // other shell routes through bookmarks, so keep direct navigation inside
  // the assessment workspace as well.
  if (assessmentOnly && location.pathname !== assessmentPath) {
    return <Navigate replace to={assessmentPath} />;
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        {locale.text("skipToContent")}
      </a>

      <header className="app-header">
        <Link aria-label={locale.text("shellBrandHomeLabel")} className="brand-lockup" to={assessmentOnly ? assessmentPath : HOME_ROUTE}>
          <EngageLogo name={locale.text("appName")} subtitle={locale.text("shellEngageSubtitle")} />
        </Link>

        <nav
          aria-label={locale.text("primaryNavigation")}
          className="header-module-nav"
        >
          {primaryModuleItems.map((item) => (
            <Link
              aria-current={activeModule === item.id ? "page" : undefined}
              className="header-module-link"
              key={item.id}
              onClick={item.onSelect}
              title={item.label}
              to={item.to}
            >
              <span aria-hidden="true" className="header-module-icon">
                <HeaderModuleGlyph item={item} />
              </span>
              <strong>{item.label}</strong>
            </Link>
          ))}
          {overflowModuleItems.length === 0 ? null : (
            <details
              className="header-more-menu"
              data-active={overflowModuleItems.some((item) => item.id === activeModule) ? "true" : undefined}
              ref={moreMenuRef}
            >
              <summary
                className="header-module-link"
                title={locale.text("shellMoreNav")}
              >
                <span aria-hidden="true" className="header-module-icon">
                  <ShellIcon name="modules" />
                </span>
                <strong>{locale.text("shellMoreNav")} <span aria-hidden="true">▾</span></strong>
              </summary>
              <div className="header-more-panel">
                {overflowModuleItems.map((item) => (
                  <Link
                    aria-current={activeModule === item.id ? "page" : undefined}
                    key={item.id}
                    onClick={(event) => {
                      // Context-backed perspective links synchronously prevent
                      // default before their authorization refresh.
                      item.onSelect?.(event);
                      moreMenuRef.current?.removeAttribute("open");
                    }}
                    to={item.to}
                  >
                    <span aria-hidden="true" className="header-module-icon">
                      <HeaderModuleGlyph item={item} />
                    </span>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </details>
          )}
        </nav>

        <div className="shell-actions">
          <AstrialLogo className="header-vendor-logo" title="Astrial" variant="full" />
          <ConnectivityStatus />
          <button
            aria-label={locale.text(fullscreen ? "shellExitFullscreen" : "shellEnterFullscreen")}
            className="shell-icon-action shell-fullscreen-action"
            onClick={toggleFullscreen}
            title={locale.text(fullscreen ? "shellExitFullscreen" : "shellEnterFullscreen")}
            type="button"
          >
            <ShellIcon name="fullscreen" />
          </button>
          {/*
            Chat is incident-scoped and permission-gated on the same capability
            its API checks. The notification inbox below is session-scoped.
          */}
          {session.canDiscover(["channel_read"]) ? (
            <Link
              aria-label={locale.text("shellChatLabel")}
              className="shell-icon-action"
              title={locale.text("shellChatLabel")}
              to={operationalContext.selection.incidentId === null
                ? "/?open=collaboration"
                : `/?open=collaboration&incidentId=${encodeURIComponent(operationalContext.selection.incidentId)}`}
            >
              <ShellIcon name="collaboration" />
            </Link>
          ) : null}
          <details className="header-notifications" ref={notificationsMenuRef}>
            <summary
              aria-label={notifications.unreadCount === 0
                ? locale.text("shellNotificationsLabel")
                : locale.code === "de"
                  ? `${locale.text("shellNotificationsLabel")}, ${String(notifications.unreadCount)} ungelesen`
                  : `${locale.text("shellNotificationsLabel")}, ${String(notifications.unreadCount)} unread`}
              className="shell-icon-action"
              title={notifications.unreadCount === 0
                ? locale.text("shellNoUrgentAlerts")
                : locale.text("shellNotificationsLabel")}
            >
              <ShellIcon name="alerts" />
              <b className={notifications.unreadCount > 0 ? "header-notifications__count--unread" : undefined}>
                {notifications.unreadCount > 99 ? "99+" : notifications.unreadCount}
              </b>
            </summary>
            <div aria-live="polite" className="header-notifications__panel">
              <div className="header-notifications__heading">
                <strong>{locale.text("shellNotificationsLabel")}</strong>
                {notifications.unreadCount > 0 ? (
                  <button
                    onClick={() => { void notifications.markAllRead(); }}
                    type="button"
                  >
                    {locale.text("shellNotificationsMarkAllRead")}
                  </button>
                ) : null}
              </div>
              {notifications.items.length === 0 ? (
                <p className="header-notifications__empty">{locale.text("shellNoUrgentAlerts")}</p>
              ) : (
                <ol className="header-notifications__list">
                  {notifications.items.map((item) => {
                    const content = presentNotification(item, locale.code);
                    const target = notificationNavigationTarget(item.contextPath);
                    const actionLabel = notificationActionLabel(item.contextPath, locale.code);
                    const tone = notificationTone(item);
                    return (
                      <li
                        className={[
                          "header-notifications__item",
                          `header-notifications__item--${tone}`,
                          item.readAt === null ? "header-notifications__item--unread" : "",
                        ].filter(Boolean).join(" ")}
                        key={item.deliveryId}
                      >
                        <button
                          onClick={() => {
                            void notifications.markRead(item.deliveryId);
                            if (target !== null) {
                              void navigate(target);
                              notificationsMenuRef.current?.removeAttribute("open");
                            }
                          }}
                          type="button"
                        >
                          <span aria-hidden="true" className="header-notifications__marker">
                            <OperationalIcon name={notificationIcons[tone]} />
                          </span>
                          <span className="header-notifications__content">
                            <strong>{content.title}</strong>
                            <span>{content.body}</span>
                            <time>
                              {new Date(item.deliveredAt).toLocaleString(locale.code, {
                                day: "2-digit", hour: "2-digit", minute: "2-digit", month: "2-digit",
                              })}
                            </time>
                            {actionLabel === null ? null : (
                              <span className="header-notifications__action">
                                {actionLabel}
                                <OperationalIcon name="send" />
                              </span>
                            )}
                          </span>
                        </button>
                        {item.acknowledgementRequired && item.acknowledgedAt === null ? (
                          <button
                            className="header-notifications__acknowledge"
                            onClick={() => { void notifications.acknowledge(item.deliveryId); }}
                            type="button"
                          >
                            {locale.text("shellNotificationsAcknowledge")}
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </details>
          <HeaderClock label={locale.text("shellCurrentTime")} localeCode={locale.code} />
          <div className="header-account-menu">
            <button
              aria-label={profileLabel}
              aria-expanded={accountMenuOpen}
              className="header-account-trigger"
              onClick={() => { setAccountMenuOpen((open) => !open); }}
              title={profileLabel}
              type="button"
            >
              <EntityPhoto
                className="header-account-avatar"
                fallback={profileInitial ?? <ShellIcon name="profile" />}
                label={profileLabel}
                src={mediaUrlForKey(session.identity?.avatarKey)}
              />
            </button>
            {accountMenuOpen ? <div className="header-account-panel">
              <div className="header-account-heading">
                <EntityPhoto
                  className="header-account-avatar header-account-avatar--large"
                  fallback={profileInitial ?? <ShellIcon name="profile" />}
                  label={profileLabel}
                  size="large"
                  src={mediaUrlForKey(session.identity?.avatarKey)}
                />
                <div>
                  <strong>{session.identity?.displayName}</strong>
                  {session.activeContext !== null ? (
                    <span>{workContextLabel(session.activeContext.roleCode, locale.code)}</span>
                  ) : null}
                  {session.activeOrganizationUnit !== null ? (
                    <small>
                      {session.isGlobalAdministrationContext
                        ? locale.code === "de" ? "Alle Organisationen" : "All organizations"
                        : session.activeOrganizationUnit.name ?? "Organization unavailable"}
                    </small>
                  ) : null}
                </div>
              </div>

              {session.clientDevice === "mobile" &&
              normalizeRoleCode(session.activeContext?.roleCode ?? "") === "field_user" ? (
                <ProfilePhotoEditor />
              ) : null}

              <SessionControls hideIdentity />
              <ProjectInformation />

              {workspaceLayoutActions === null ? null : (
                <section
                  aria-label={locale.code === "de" ? "Arbeitsbereich-Layout" : "Workspace layout"}
                  className="header-account-layout"
                >
                  <h2>{locale.code === "de" ? "Arbeitsbereich-Layout" : "Workspace layout"}</h2>
                  <div>
                    <Button onClick={workspaceLayoutActions.reset}>
                      {workspaceText(locale.code, "resetLayout")}
                    </Button>
                    <Button
                      onClick={() => {
                        void workspaceLayoutActions.save();
                      }}
                      variant="primary"
                    >
                      {workspaceText(locale.code, "saveLayout")}
                    </Button>
                  </div>
                </section>
              )}

              <div className="header-account-preferences">
                <div
                  aria-label={locale.text("languageLabel")}
                  className="header-account-preference"
                  role="group"
                >
                  <span>{locale.text("languageLabel")}</span>
                  <div className="thw-segmented">
                    <button
                      aria-pressed={locale.code === "de"}
                      className="thw-segmented__option"
                      onClick={() => void locale.setLocale("de")}
                      type="button"
                    >
                      Deutsch
                    </button>
                    <button
                      aria-pressed={locale.code === "en"}
                      className="thw-segmented__option"
                      onClick={() => void locale.setLocale("en")}
                      type="button"
                    >
                      English
                    </button>
                  </div>
                </div>
                <div
                  aria-label={locale.text("themeLabel")}
                  className="header-account-preference"
                  role="group"
                >
                  <span>{locale.text("themeLabel")}</span>
                  <div className="thw-swatch-row">
                    {themeCodes.map((code) => (
                      <button
                        aria-pressed={theme.theme === code}
                        className={`thw-swatch thw-swatch--${code}`}
                        key={code}
                        onClick={() => { theme.setTheme(code); }}
                        title={locale.text(themeMessageKeys[code])}
                        type="button"
                      >
                        <span aria-hidden="true" className="thw-swatch__chip" />
                        <span className="thw-swatch__label">{locale.text(themeMessageKeys[code])}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {status.updateAvailable ? (
                <button onClick={() => { status.requestUpdate(); }} type="button">
                  {locale.text("applyUpdate")}
                </button>
              ) : null}
            </div> : null}
          </div>
        </div>
      </header>

      <IncidentContextBar />

      <main className="content" id="main-content" tabIndex={-1}>
        <Outlet />
      </main>

      {routeOwnsMobileNavigation ? null : <nav
        aria-label={locale.text("workspaceMobileNavigation")}
        className="mobile-bottom-navigation"
      >
        {mobileModuleItems.map((item) => (
          <Link
            aria-current={activeModule === item.id ? "page" : undefined}
            className="mobile-bottom-navigation__item"
            key={item.id}
            onClick={item.onSelect}
            to={item.to}
          >
            <span aria-hidden="true" className="mobile-bottom-navigation__icon">
              <HeaderModuleGlyph item={item} />
            </span>
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>}
    </div>
  );
});
